/**
 * Popup or redirect for Google sign-in (ROADMAP 1.37, execution #2).
 *
 * A phone opening a letter's link often lands in a mail app's in-app browser,
 * which blocks the popup outright; iOS Safari suppresses popups too. The
 * redirect flow survives both — but ONLY when the auth domain is this page's
 * own origin: browsers that partition third-party storage (Safari 16.1+,
 * current Chrome) lose a cross-origin redirect's state and the sign-in fails
 * silently. Prod builds with `authDomain = indexalpha.ai` (same origin);
 * staging and PR previews share that auth domain from another host, so there
 * the popup stays the only flow.
 */

/** Popup errors a redirect can recover from. */
export const REDIRECTABLE_POPUP_ERRORS: ReadonlySet<string> = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
]);

export interface SignInEnvironment {
  hostname: string;
  authDomain?: string;
  /** A touch phone: `(pointer: coarse)` and below the phone layout's `md` line. */
  touchPhone: boolean;
}

/** True when a redirect can complete here at all. */
export function redirectWorksHere(env: SignInEnvironment): boolean {
  return !!env.authDomain && env.hostname.toLowerCase() === env.authDomain.toLowerCase();
}

/** Which flow to START with. A popup that is then blocked may still fall back. */
export function googleSignInMode(env: SignInEnvironment): 'popup' | 'redirect' {
  return env.touchPhone && redirectWorksHere(env) ? 'redirect' : 'popup';
}

/** Whether a failed popup should retry as a redirect. */
export function shouldFallBackToRedirect(errorCode: string | undefined, env: SignInEnvironment): boolean {
  return !!errorCode && REDIRECTABLE_POPUP_ERRORS.has(errorCode) && redirectWorksHere(env);
}

/** The environment this browser is in, right now. */
export function currentSignInEnvironment(authDomain: string | undefined): SignInEnvironment {
  const mq = (q: string) => typeof window.matchMedia === 'function' && window.matchMedia(q).matches;
  return {
    hostname: window.location.hostname,
    authDomain,
    touchPhone: mq('(pointer: coarse)') && mq('(max-width:899.95px)'),
  };
}
